import { create } from 'zustand';
import Web3 from 'web3';
import { useConnectionStore } from './connection';
import { TxStatus, OnFinishData } from './tx';
import { useWalletEnvStore } from './wallet-env';
import { useBalanceStore } from './balance';
import { isElectron } from '@shared/utils/is-electron';
import { LIFTING_CHAINS } from '@shared/types/enums';
import isOnline from 'is-online';
import { useWalletStore } from './wallets';

type ClaimLowerStore = {
  status: TxStatus;
  claimLower: (
    receiverAddress: string,
    lowerProof: string,
    network: LIFTING_CHAINS,
    onFinish: (onFinishData: OnFinishData) => void,
  ) => Promise<void>;
  reset: () => void;
};

export const useClaimLowerStore = create<ClaimLowerStore>((set) => ({
  status: 'idle',
  claimLower: async (
    receiverAddress: string,
    lowerProof: string,
    network: LIFTING_CHAINS,
    onFinish: (onFinishData: OnFinishData) => void,
  ) => {
    try {
      const { client, sessionETH } = useConnectionStore.getState();
      const { walletConst } = useWalletEnvStore.getState();
      const { setBalanceETH, fetchNetworkBalance } = useBalanceStore.getState();
      const { walletType } = useWalletStore.getState();

      // Check if using Ledger wallet
      // if (walletType === 'ledger' && isElectron()) {
      //   const api = useApiServiceStore.getState().api;
      //   const result = await api.deviceLifting(receiverAddress, lowerProof, network);

      //   if (result.errorStatus) {
      //     set({ status: 'error' });
      //     onFinish({ status: 'error', errorMsg: result.errorStatus });
      //     return;
      //   }

      //   set({ status: 'success' });
      //   onFinish({ status: 'success', blockHash: result.txnHash });
      //   return;
      // }

      // WalletConnect flow
      if (!sessionETH) {
        throw new Error(`No ${network} session`);
      }

      if (!client) {
        throw new Error('Client not initialized');
      }

      set({ status: 'preparing' });

      // Get network-specific RPC endpoint and configuration
      const rpcEndpoint = walletConst.ethRpc;
      const chainId = walletConst.ethChainId;

      if (!rpcEndpoint) {
        throw new Error(`${network} RPC endpoint is not configured`);
      }

      if (!chainId) {
        throw new Error(`${network} chain ID is not configured`);
      }

      const httpProvider = new Web3.providers.HttpProvider(rpcEndpoint);
      const web3 = new Web3(httpProvider);

      const contractAbi = walletConst.ethAbi;
      const contractAddress = walletConst.ethSmartContract;

      const ftsmContract = new web3.eth.Contract(contractAbi, contractAddress);

      const senderAddress = String(sessionETH.namespaces.eip155.accounts[0].split(':')[2]);

      try {
        const checkResult = await ftsmContract.methods.checkLower(lowerProof).call();

        // Check if the proof is valid and not already claimed
        if (!checkResult.proofIsValid) {
          throw new Error('Lower proof is not valid according to smart contract');
        }

        if (checkResult.lowerIsClaimed) {
          throw new Error('Lower has already been claimed');
        }
      } catch (validationError: Error | unknown) {
        console.error(validationError);
        throw new Error(
          `Lower validation failed: ${validationError instanceof Error ? validationError.message : 'Unknown error'}`,
        );
      }

      const gas = await ftsmContract.methods
        .claimLower(lowerProof)
        .estimateGas({ from: senderAddress });
      const transactionData = ftsmContract.methods.claimLower(lowerProof);
      const gasPrice = await web3.eth.getGasPrice();
      const gasLimitWithMargin = Math.floor(gas * 1.2);

      const tx = {
        from: senderAddress,
        to: contractAddress,
        data: transactionData.encodeABI(),
        value: '0',
        nonce: Web3.utils.toHex(await web3.eth.getTransactionCount(senderAddress)),
        gas: gas,
        gasPrice: gasPrice,
        gasLimit: gasLimitWithMargin,
      };

      set({ status: 'pending-confirmation' });

      const txnHash = await Promise.race([
        client.request({
          chainId: `eip155:${chainId}`,
          topic: sessionETH.topic,
          request: {
            method: 'eth_sendTransaction',
            params: [tx],
          },
        }) as Promise<string>,
        (async () => {
          let isInternet = await isOnline();
          /**
           * on Electron app it should just do a while loop until approved in wallet (no breaking change)
           * on Web app it's going to stop when there's no internet connection and this would resolve Promise.race with undefined
           */
          while (isInternet || isElectron()) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            isInternet = await isOnline();
          }

          if (!isInternet && !isElectron) {
            return;
          }
        })(),
      ]);

      if (!txnHash) {
        set({ status: 'error' });
        onFinish({ status: 'error', errorMsg: 'Waiting for approval failed - no internet' });
        return;
      }

      set({ status: 'executing' });
      // TODO: Check if the transaction was successful
      await new Promise((resolve) => setTimeout(resolve, 1000));
      set({ status: 'success' });
      onFinish({ status: 'success', blockHash: txnHash });
    } catch (error) {
      let errorMsg: string;
      if (error instanceof Error) {
        errorMsg = error.message;
      } else if (typeof error === 'object' && error?.['message']) {
        errorMsg = error['message'];
      } else {
        errorMsg = 'An unknown error occurred';
      }

      set({ status: 'error' });
      onFinish({ status: 'error', errorMsg });
      console.error(error);
    }
  },
  reset: () => set({ status: 'idle' }),
}));
